import { useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  Text,
  View,
  type ImageSourcePropType,
} from "react-native";
import {
  AlertCircle,
  Beaker,
  BookOpen,
  CalendarDays,
  CheckCircle,
  ClipboardCheck,
  Clock3,
  DollarSign,
  FileText,
  Heart,
  History,
  Info,
  Plus,
  TestTube2,
  Timer,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { CommonActions } from "@react-navigation/native";
import {
  getBlocksByKind,
  getDataPointsForProduct,
  getDataPointsForTest,
  getTestsForDataPoint,
  getCollectionLabel,
  makeId,
  type ContentBlock,
  type ContentBlockId,
  type DataPointBody,
  type DataPointSampleType,
  type TestBody,
} from "@src/packages/content";
import { useLocale } from "@src/packages/i18n/useLocale";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import {
  getBiomarkerIdentity,
  groupCanonicalResults,
  type CanonicalTestResult,
  useCanonicalTestResults,
} from "@src/packages/test-results";
import {
  useDeleteTestResult,
  useUpdateTestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import {
  getBiomarkerFactsheet,
  type BiomarkerFactsheet,
} from "@src/packages/test-results/content/biomarker-factsheets";
import {
  DEV_PREVIEW_PANEL_MIXED_RESULTS,
  DEV_PREVIEW_PANEL_RESULTS,
  getDevPreviewLabResultsState,
  isDevPreviewLabResultsEnabled,
} from "../../dev/labResultsFixture";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { IconButton, SectionTitle, SectionTitleRow } from "@louise/moon-ui";
import FactSheetHeader, {
  type FactSheetHeaderBadge,
} from "../../components/FactSheetHeader";
import AccessGridCard, {
  AccessGridCardPill,
} from "../../components/access/AccessGridCard";
import FactSheetTabControl, {
  type FactSheetTab,
} from "../../components/FactSheetTabControl";
import EvidenceLevelModal from "../../components/EvidenceLevelModal";
import EvidenceSourceRows from "../../components/EvidenceSourceRows";
import TestResultActionsCard from "../../components/TestResultActionsCard";
import { HistoryChart } from "../../components/HistoryChart";
import {
  DocumentRow,
  DocumentTestResultRow,
  type DocumentTestResultCategory,
} from "../documents/DocumentRow";
import type {
  LabResult,
  LabResultHistoryPoint,
} from "../care/sections/ResultsLabsCard";
import TestResultDetailDialog from "../../components/TestResultDetailDialog";
import { useDateFormat } from "@src/packages/utils/useDateFormat";
import testTubesIllustration from "../../../assets/images/you-icons/test-tubes.png";
import { getTestIllustration } from "../../components/access/testIllustrations";

type Props = NativeStackScreenProps<
  AccessStackParamList,
  "TestPanelComparison"
>;
type PanelTab = "overview" | "values" | "biology" | "testing";
type TestQuickFact = NonNullable<TestBody["quickFacts"]>[number];
type ResultPoint = LabResultHistoryPoint;
type DataPointBlock = ContentBlock<"data-point">;
type PanelOption = ContentBlock<"test"> | ContentBlock<"product">;

const QUICK_FACT_ICONS: Record<TestQuickFact["kind"], LucideIcon> = {
  sample: Beaker,
  time: Timer,
  pain: Clock3,
  anesthesia: Heart,
  prep: ClipboardCheck,
  results: FileText,
};

const QUICK_FACT_LABEL_KEYS: Record<TestQuickFact["kind"], string> = {
  sample: "learn.test_detail.quick_fact_sample",
  time: "learn.test_detail.quick_fact_time",
  pain: "learn.test_detail.quick_fact_pain",
  anesthesia: "learn.test_detail.quick_fact_anesthesia",
  prep: "learn.test_detail.quick_fact_prep",
  results: "learn.test_detail.quick_fact_results",
};

type PanelMarker = {
  testId: string;
  dataPoint: DataPointBlock;
  body: DataPointBody;
  factsheet: BiomarkerFactsheet | null;
  result: LabResult | null;
  markerTestId?: string;
  markerTestIsSingleMarker?: boolean;
};

const AFC_TEST_ID = makeId("test-afc");
const FOLLICLE_COUNT_DATA_POINT_ID = makeId("dp-follicle-count");

function getPanelMarkerLabel(marker: PanelMarker): string {
  if (
    marker.testId === AFC_TEST_ID &&
    marker.dataPoint.id === FOLLICLE_COUNT_DATA_POINT_ID
  ) {
    return "AFC";
  }

  return marker.dataPoint.title;
}

const SAMPLE_LABEL_KEYS: Record<DataPointSampleType, string> = {
  blood: "care.lab_result.sample_blood",
  saliva: "care.lab_result.sample_saliva",
  urine: "care.lab_result.sample_urine",
  semen: "care.lab_result.sample_semen",
  ultrasound: "care.lab_result.sample_ultrasound",
  imaging: "care.lab_result.sample_imaging",
  genetic: "care.lab_result.sample_genetic",
  swab: "care.lab_result.sample_swab",
  tissue: "care.lab_result.sample_tissue",
};

function getPrimarySampleType(
  testBody: TestBody,
  markers: PanelMarker[],
): DataPointSampleType | undefined {
  switch (testBody.category) {
    case "monitoring":
    case "imaging":
      return "ultrasound";
    case "blood-marker":
      return "blood";
    case "semen":
      return "semen";
    default:
      return markers
        .flatMap((marker) => marker.body.sampleTypes ?? [])
        .find(Boolean);
  }
}

function getTestImage(
  testId: ContentBlockId,
  sampleType: DataPointSampleType | undefined,
  dataPoints: DataPointBlock[],
  category?: TestBody["category"],
): ImageSourcePropType {
  return getTestIllustration({
    testId: String(testId),
    category,
    sampleType,
    dataPointIds: dataPoints.map((dataPoint) => String(dataPoint.id)),
  });
}

function getMarkerImage(marker: PanelMarker): ImageSourcePropType {
  return getTestIllustration({
    testId: marker.testId,
    sampleType: marker.body.sampleTypes?.find(Boolean),
    dataPointIds: [String(marker.dataPoint.id)],
    displayContext: "marker",
  });
}

function getOptionImage(option: PanelOption): ImageSourcePropType {
  const dataPoints =
    option.kind === "test"
      ? getDataPointsForTest(option.id)
      : getDataPointsForProduct(option.id);
  const sampleType = dataPoints
    .flatMap((dataPoint) => dataPoint.body.sampleTypes ?? [])
    .find(Boolean);

  return getTestIllustration({
    testId: String(option.id),
    category: option.kind === "test" ? option.body.category : undefined,
    sampleType,
    dataPointIds: dataPoints.map((dataPoint) => String(dataPoint.id)),
  });
}

function getTestBlock(id: string): ContentBlock<"test"> | null {
  const candidateIds = id.startsWith("test-") ? [id] : [id, `test-${id}`];
  return (
    getBlocksByKind("test").find((block) =>
      candidateIds.some((candidateId) => block.id === makeId(candidateId)),
    ) ?? null
  );
}

function getResultCategory(result: LabResult): DocumentTestResultCategory {
  switch (result.clinicalAssessment) {
    case "low":
      return "low";
    case "high":
      return "high";
    case "normal":
      return "normal";
    default:
      if (result.isAbnormal) return "borderline";
      if (result.trend === "up") return "high";
      if (result.trend === "down") return "low";
      return "normal";
  }
}

function buildLabResult(
  dataPoint: DataPointBlock,
  factsheet: BiomarkerFactsheet | null,
  results: CanonicalTestResult[],
  formatDate: (date: string) => string,
): LabResult | null {
  const canonicalHistory = groupCanonicalResults(results)[0];
  if (!canonicalHistory) return null;
  const latest = canonicalHistory.latest;

  return {
    id: latest.id,
    dataPointId: canonicalHistory.dataPointId,
    name: latest.name ?? dataPoint.title,
    value: latest.value,
    unit: latest.unit ?? dataPoint.body.unit ?? "",
    date: latest.date ? formatDate(latest.date) : "",
    cycleId: latest.cycleId,
    trend: canonicalHistory.trend,
    clinicalAssessment: latest.clinicalAssessment,
    clinicalAssessmentDetails: latest.clinicalAssessmentDetails,
    attachment: latest.source.attachment,
    provenance: latest.source,
    isAbnormal:
      latest.clinicalAssessment === "low" ||
      latest.clinicalAssessment === "high",
    history: canonicalHistory.results.map((result) => ({
      id: result.id,
      date: result.date ? formatDate(result.date) : "",
      value: result.value,
      clinicalAssessment: result.clinicalAssessment,
      clinicalAssessmentDetails: result.clinicalAssessmentDetails,
      attachment: result.source.attachment,
      provenance: result.source,
    })),
    subtitle: factsheet?.description ?? dataPoint.body.interpretation,
    description: factsheet?.description ?? dataPoint.body.interpretation,
    whyItMatters: factsheet?.whyItMatters,
    normalRange: factsheet?.normalRange ?? dataPoint.body.normalRange,
    normalRangeBullets: factsheet?.normalRangeBullets,
    biology: factsheet?.biology,
    measurementDetails: factsheet?.measurementDetails,
    biologicalVariation: factsheet?.biologicalVariation,
    relatedMarkers: factsheet?.relatedMarkers,
  };
}

function InfoBlock({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: string;
}) {
  return (
    <View style={{ gap: 8 }}>
      <SectionTitle icon={Icon}>{title}</SectionTitle>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 14, color: "#6E625B", lineHeight: 21 }}
      >
        {children}
      </Text>
    </View>
  );
}

function QuickFacts({
  items,
  t,
}: {
  items: NonNullable<TestBody["quickFacts"]>;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
      {items.map((item, index) => {
        const Icon = QUICK_FACT_ICONS[item.kind];
        return (
          <View
            key={`${item.kind}-${index}`}
            style={{
              flexBasis: "47%",
              flexGrow: 1,
              minWidth: 140,
              minHeight: 78,
              borderRadius: 10,
              borderWidth: 1,
              borderColor: "rgba(0,0,0,0.06)",
              backgroundColor: "rgba(255,255,255,0.3)",
              paddingHorizontal: 12,
              paddingVertical: 11,
            }}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 7,
                marginBottom: 7,
              }}
            >
              <View
                style={{
                  width: 23,
                  height: 23,
                  borderRadius: 12,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(88,78,68,0.1)",
                }}
              >
                <Icon size={13} color="#6E625B" strokeWidth={1.8} />
              </View>
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 10,
                  fontWeight: "700",
                  color: "#9A8E85",
                  textTransform: "uppercase",
                }}
              >
                {item.label ?? t(QUICK_FACT_LABEL_KEYS[item.kind])}
              </Text>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "600",
                color: "#6E625B",
                lineHeight: 18,
              }}
            >
              {item.value}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

function StepList({ items }: { items: string[] }) {
  return (
    <View
      style={{
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
        backgroundColor: "rgba(255,255,255,0.3)",
        overflow: "hidden",
      }}
    >
      {items.map((item, index) => (
        <View
          key={`${item.slice(0, 20)}-${index}`}
          style={{
            flexDirection: "row",
            gap: 10,
            padding: 12,
            borderTopWidth: index === 0 ? 0 : 1,
            borderTopColor: "rgba(0,0,0,0.05)",
          }}
        >
          <View
            style={{
              width: 22,
              height: 22,
              borderRadius: 11,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(88,78,68,0.1)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, fontWeight: "700", color: "#6E625B" }}
            >
              {index + 1}
            </Text>
          </View>
          <Text
            className="font-dm-sans"
            style={{ flex: 1, fontSize: 13, lineHeight: 19, color: "#7D6F66" }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function BenefitList({ items }: { items: string[] }) {
  return (
    <View style={{ gap: 10 }}>
      {items.map((item, index) => (
        <View
          key={`${item.slice(0, 20)}-${index}`}
          style={{
            flexDirection: "row",
            gap: 10,
            paddingHorizontal: 12,
            paddingVertical: 12,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.28)",
          }}
        >
          <View
            style={{
              width: 24,
              height: 24,
              borderRadius: 12,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(86,124,84,0.10)",
              marginTop: 1,
            }}
          >
            <CheckCircle size={13} color="#567C54" strokeWidth={2} />
          </View>
          <Text
            className="font-dm-sans"
            style={{ flex: 1, fontSize: 13, color: "#7D6F66", lineHeight: 18 }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  );
}

function MarkerTiming({
  items,
}: {
  items: NonNullable<TestBody["markerTiming"]>;
}) {
  return (
    <View style={{ gap: 10 }}>
      {items.map((item, index) => (
        <View
          key={`${item.marker}-${index}`}
          style={{
            flexDirection: "row",
            gap: 12,
            padding: 12,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: "rgba(0,0,0,0.06)",
            backgroundColor: "rgba(255,255,255,0.3)",
          }}
        >
          <CalendarDays size={17} color="#6E625B" strokeWidth={1.7} />
          <View style={{ flex: 1, gap: 4 }}>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 14, fontWeight: "700", color: "#6E625B" }}
            >
              {item.marker}
            </Text>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 13, fontWeight: "600", color: "#7D6F66" }}
            >
              {item.timing}
            </Text>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, lineHeight: 17, color: "#9A8E85" }}
            >
              {item.reason}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

function PanelMarkerCard({
  marker,
  onPress,
  t,
}: {
  marker: PanelMarker;
  onPress?: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  const capturePress = usePressCapture("PanelMarkerCard");
  const result = marker.result;
  const markerLabel = getPanelMarkerLabel(marker);
  return (
    <Pressable
      accessibilityRole={onPress ? "button" : undefined}
      onPress={
        onPress
          ? () => {
              capturePress(pressLabel(markerLabel), "panel_marker_card");
              onPress();
            }
          : undefined
      }
      style={({ pressed }) => ({
        flexBasis: "47%",
        flexGrow: 1,
        minWidth: 140,
        minHeight: 112,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
        backgroundColor: pressed
          ? "rgba(255,255,255,0.55)"
          : "rgba(255,255,255,0.3)",
        padding: 13,
        opacity: onPress && pressed ? 0.78 : 1,
      })}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 7 }}>
        <View
          style={{
            width: 25,
            height: 25,
            borderRadius: 13,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(88,78,68,0.1)",
          }}
        >
          <Beaker size={14} color="#6E625B" strokeWidth={1.7} />
        </View>
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{ flex: 1, fontSize: 13, fontWeight: "700", color: "#6E625B" }}
        >
          {markerLabel}
        </Text>
      </View>
      <View style={{ marginTop: 13 }}>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 19, fontWeight: "700", color: "#6E625B" }}
        >
          {result?.value ??
            t("access.testing.comparison.panel_no_result_value")}
          {result?.unit ? (
            <Text style={{ fontSize: 11, fontWeight: "500", color: "#9A8E85" }}>
              {` ${result.unit}`}
            </Text>
          ) : null}
        </Text>
        <Text
          className="font-dm-sans"
          numberOfLines={1}
          style={{ marginTop: 4, fontSize: 11, color: "#9A8E85" }}
        >
          {result?.date ??
            t("access.testing.comparison.panel_marker_not_logged", {
              params: { marker: markerLabel },
            })}
        </Text>
      </View>
    </Pressable>
  );
}

function PanelAddResultRow({
  onAddResult,
  t,
}: {
  onAddResult: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  return (
    <DocumentRow
      title={t("care.lab_result.add_value")}
      subtitle={t("access.testing.comparison.panel_add_result_body")}
      imageSource={testTubesIllustration}
      trailing={<Plus size={18} color="#9A8E85" strokeWidth={1.5} />}
      onPress={onAddResult}
      showChevron={false}
    />
  );
}

function PanelMissingResultRow({
  marker,
  onAddResult,
  t,
}: {
  marker: PanelMarker;
  onAddResult?: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  const markerLabel = getPanelMarkerLabel(marker);
  return (
    <DocumentRow
      title={t("access.testing.comparison.panel_no_marker_result_title", {
        params: { marker: markerLabel },
      })}
      subtitle={t("access.testing.comparison.panel_marker_not_logged", {
        params: { marker: markerLabel },
      })}
      imageSource={getMarkerImage(marker)}
      trailing={
        onAddResult ? (
          <Plus size={18} color="#9A8E85" strokeWidth={1.5} />
        ) : undefined
      }
      onPress={onAddResult}
      showChevron={false}
    />
  );
}

function PanelOverviewResultRow({
  marker,
  onOpenDetails,
  onAddResult,
  t,
}: {
  marker: PanelMarker;
  onOpenDetails: (result: LabResult, point: ResultPoint) => void;
  onAddResult: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  const result = marker.result;
  if (!result) {
    return (
      <PanelMissingResultRow marker={marker} onAddResult={onAddResult} t={t} />
    );
  }

  const point = {
    id: result.id,
    date: result.date,
    value: result.value,
  };
  return (
    <DocumentTestResultRow
      title={getPanelMarkerLabel(marker)}
      subtitle={result.subtitle ?? marker.body.interpretation ?? ""}
      dateLabel={result.date}
      value={result.value}
      unit={result.unit}
      category={getResultCategory(result)}
      imageSource={getMarkerImage(marker)}
      onPress={() => onOpenDetails(result, point)}
    />
  );
}

function PanelOverviewSummaryRow({
  test,
  markers,
  onResultPress,
  onAddResult,
  onGetTested,
  t,
}: {
  test: ContentBlock<"test">;
  markers: PanelMarker[];
  onResultPress: () => void;
  onAddResult: () => void;
  onGetTested: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  const markersWithResults = markers.filter((marker) => marker.result);
  if (!markersWithResults.length) {
    return (
      <TestResultActionsCard
        addResultDescription={t(
          "access.testing.comparison.test_not_logged_description",
          { params: { marker: test.title } },
        )}
        getTestedDescription={t(
          "access.testing.comparison.get_tested_description",
        )}
        onAddResult={onAddResult}
        onGetTested={onGetTested}
        t={t}
      />
    );
  }

  const latestMarker = markersWithResults.reduce<PanelMarker | null>(
    (latest, marker) => {
      if (!marker.result) return latest;
      if (!latest?.result) return marker;
      return new Date(marker.result.date).getTime() >=
        new Date(latest.result.date).getTime()
        ? marker
        : latest;
    },
    null,
  );
  if (!latestMarker?.result) return null;

  const result = latestMarker.result;
  const markerLabel = getPanelMarkerLabel(latestMarker);
  return (
    <DocumentRow
      title={test.title}
      subtitle={t("access.testing.comparison.panel_overview_summary", {
        params: {
          completed: String(markersWithResults.length),
          total: String(markers.length),
          marker: markerLabel,
        },
      })}
      dateLabel={result.date}
      imageSource={getMarkerImage(latestMarker)}
      onPress={onResultPress}
    />
  );
}

function PanelMarkerValues({
  marker,
  onOpenDetails,
  t,
}: {
  marker: PanelMarker;
  onOpenDetails: (result: LabResult, point: ResultPoint) => void;
  t: ReturnType<typeof useTranslations>;
}) {
  const result = marker.result;
  const markerLabel = getPanelMarkerLabel(marker);
  if (!result) {
    return (
      <View style={{ gap: 9 }}>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 15, fontWeight: "700", color: "#6E625B" }}
        >
          {markerLabel}
        </Text>
        <PanelMissingResultRow marker={marker} t={t} />
      </View>
    );
  }

  const points =
    result.history && result.history.length > 0
      ? result.history
      : [{ id: result.id, date: result.date, value: result.value }];
  const latestFirstPoints = [...points].reverse();
  const subtitle = result.subtitle ?? marker.body.interpretation ?? "";

  return (
    <View style={{ gap: 9 }}>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 15, fontWeight: "700", color: "#6E625B" }}
      >
        {markerLabel}
      </Text>
      {points.length > 1 ? (
        <HistoryChart
          history={points}
          unit={result.unit}
          isAbnormal={result.isAbnormal}
        />
      ) : null}
      <View style={{ gap: 9 }}>
        {latestFirstPoints.map((point) => (
          <DocumentTestResultRow
            key={point.id}
            title={markerLabel}
            subtitle={subtitle}
            dateLabel={point.date}
            value={point.value}
            unit={result.unit}
            category={getResultCategory(result)}
            imageSource={getMarkerImage(marker)}
            onPress={() => onOpenDetails(result, point)}
          />
        ))}
      </View>
    </View>
  );
}

function TestOptionCard({
  option,
  imageSource,
  onPress,
  t,
}: {
  option: PanelOption;
  imageSource: ImageSourcePropType;
  onPress: () => void;
  t: ReturnType<typeof useTranslations>;
}) {
  const dataPoints =
    option.kind === "product"
      ? getDataPointsForProduct(option.id)
      : getDataPointsForTest(option.id);
  const parameterNames = dataPoints.map((dataPoint) => dataPoint.title);
  const parameterSummary = parameterNames.length
    ? [
        t("access.testing.comparison.panel_parameter_count", {
          params: { count: String(parameterNames.length) },
        }),
        parameterNames.slice(0, 2).join(" · "),
        parameterNames.length > 2
          ? "+" + String(parameterNames.length - 2) + " more"
          : undefined,
      ]
        .filter(Boolean)
        .join(" · ")
    : undefined;
  const cost =
    option.kind === "product"
      ? `$${option.body.price}`
      : option.body.costRange
        ? `$${option.body.costRange.low}–$${option.body.costRange.high}`
        : undefined;
  return (
    <View style={{ width: "48%" }}>
      <AccessGridCard
        title={option.title}
        description={parameterSummary ?? option.subtitle}
        imageSource={imageSource}
        imageStyle={{ width: 66, height: 84 }}
        onPress={onPress}
        tags={
          <>
            {option.kind === "product"
              ? option.body.chips
                  ?.slice(0, 2)
                  .map((chip) => <AccessGridCardPill key={chip} label={chip} />)
              : [
                  <AccessGridCardPill
                    key="collection"
                    label={getCollectionLabel(option.body)}
                    icon={TestTube2}
                  />,
                  option.body.turnaroundTime ? (
                    <AccessGridCardPill
                      key="turnaround"
                      label={option.body.turnaroundTime}
                      icon={Timer}
                    />
                  ) : null,
                ]}
            {cost ? (
              <AccessGridCardPill key="cost" label={cost} icon={DollarSign} />
            ) : null}
          </>
        }
      />
    </View>
  );
}

export default function TestPanelComparisonScreen({
  navigation,
  route,
}: Props) {
  const t = useTranslations();
  const { locale } = useLocale();
  const { abbrev } = useDateFormat();
  const handleBack = useSmartBack({
    returnTab: route.params.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "Testing",
  });
  const testResultsQuery = useCanonicalTestResults();
  const updateTestResult = useUpdateTestResult();
  const deleteTestResult = useDeleteTestResult();
  const devPreviewLabResults = isDevPreviewLabResultsEnabled();
  const devPreviewLabResultsState = getDevPreviewLabResultsState();
  const [activeTab, setActiveTab] = useState<PanelTab>("overview");
  const [showAllMarkers, setShowAllMarkers] = useState(false);
  const [selected, setSelected] = useState<{
    result: LabResult;
    point: ResultPoint;
  } | null>(null);
  const [evidenceLevel, setEvidenceLevel] = useState<string | null>(null);

  const test = getTestBlock(route.params.testId);
  const markerRows = useMemo<PanelMarker[]>(() => {
    if (!test) return [];
    const dataPoints = getDataPointsForTest(test.id);
    return dataPoints.map((dataPoint) => {
      const body = dataPoint.body;
      const markerResults = [...(testResultsQuery.data ?? [])].filter(
        (result) =>
          getBiomarkerIdentity(result.name) ===
          getBiomarkerIdentity(dataPoint.title),
      );
      const linkedTests = getTestsForDataPoint(dataPoint.id).filter(
        (linkedTest) => linkedTest.id !== test.id,
      );
      const markerQuery = dataPoint.title.toLowerCase();
      const markerTest =
        linkedTests.find(
          (linkedTest) =>
            getDataPointsForTest(linkedTest.id).length === 1 &&
            [linkedTest.title, linkedTest.body.testName].some((value) =>
              value?.toLowerCase().includes(markerQuery),
            ),
        ) ??
        linkedTests.find(
          (linkedTest) => getDataPointsForTest(linkedTest.id).length === 1,
        ) ??
        linkedTests.find((linkedTest) =>
          [linkedTest.title, linkedTest.subtitle].some((value) =>
            value?.toLowerCase().includes(markerQuery),
          ),
        ) ??
        linkedTests[0];
      const previewResults =
        devPreviewLabResultsState === "mixed"
          ? DEV_PREVIEW_PANEL_MIXED_RESULTS
          : DEV_PREVIEW_PANEL_RESULTS;
      const previewResult = devPreviewLabResults
        ? (previewResults.find(
            (result) =>
              getBiomarkerIdentity(result.name) ===
              getBiomarkerIdentity(dataPoint.title),
          ) ?? null)
        : null;
      return {
        testId: String(test.id),
        dataPoint,
        body,
        factsheet: getBiomarkerFactsheet(dataPoint.title, locale),
        result:
          previewResult ??
          buildLabResult(
            dataPoint,
            getBiomarkerFactsheet(dataPoint.title, locale),
            markerResults,
            abbrev,
          ),
        markerTestId: markerTest ? String(markerTest.id) : undefined,
        markerTestIsSingleMarker: markerTest
          ? getDataPointsForTest(markerTest.id).length === 1
          : undefined,
      };
    });
  }, [
    abbrev,
    devPreviewLabResults,
    devPreviewLabResultsState,
    locale,
    test,
    testResultsQuery.data,
  ]);

  const openAllResults = () => {
    navigation.getParent()?.dispatch(
      CommonActions.navigate({
        name: "Care",
        params: { screen: "AllLabResults" },
      }),
    );
  };

  const handleEditResult = async (
    id: string,
    data: { patientValue?: string; unit?: string; date?: string },
  ) => {
    await updateTestResult.mutateAsync({ testResultId: id, data });
    setSelected(null);
  };

  const handleDeleteResult = async (id: string) => {
    await deleteTestResult.mutateAsync(id);
    setSelected(null);
  };

  if (!test) return null;
  const testBody = test.body;
  const semenPanelProducts = getBlocksByKind("product")
    .filter(
      (product) =>
        product.body.section === testBody.section &&
        product.body.includedDataPointIds?.length,
    )
    .sort((left, right) => {
      const order = [
        "core",
        "post-vasectomy",
        "vasectomy-reversal",
        "advanced",
        "complete",
      ];
      return (
        order.indexOf(left.body.panelKind ?? "") -
        order.indexOf(right.body.panelKind ?? "")
      );
    });
  const howItWorksSteps =
    testBody.howItWorksSteps ??
    testBody.howItWorks
      .split(/(?<=[.!?])\s+/)
      .map((step) => step.trim())
      .filter(Boolean);
  const visibleMarkerRows = showAllMarkers
    ? markerRows
    : markerRows.slice(0, 6);
  const isMultiMarkerPanel = markerRows.length > 3;
  const hasPanelResult = markerRows.some((marker) => marker.result);
  const primarySampleType = getPrimarySampleType(testBody, markerRows);
  const tabs: FactSheetTab<PanelTab>[] = [
    {
      value: "overview",
      label: t("care.lab_result.tab_overview"),
      shortLabel: t("care.lab_result.tab_overview_short"),
      icon: Info,
    },
    {
      value: "values",
      label: t("care.lab_result.tab_values"),
      shortLabel: t("care.lab_result.tab_values_short"),
      icon: History,
    },
    {
      value: "biology",
      label: t("care.lab_result.tab_biology"),
      shortLabel: t("care.lab_result.tab_biology_short"),
      icon: Beaker,
    },
    {
      value: "testing",
      label: t("access.testing.comparison.get_tested"),
      shortLabel: t("access.testing.comparison.get_tested_short"),
      icon: TestTube2,
    },
  ];
  const badges: FactSheetHeaderBadge[] = [
    {
      label: primarySampleType
        ? t(SAMPLE_LABEL_KEYS[primarySampleType])
        : t("access.testing.comparison.blood_test"),
      icon: primarySampleType === "ultrasound" ? undefined : Beaker,
    },
    ...(testBody.atHome &&
    !testBody.clinicRequired &&
    /lab/i.test(test.subtitle ?? "")
      ? [{ label: t("access.testing.comparison.also_available_at_home") }]
      : []),
    ...(testBody.clinicRequired
      ? [{ label: t("learn.test_detail.badge_clinic_visit") }]
      : []),
  ];
  const relatedTests = Array.from(
    new Map(
      markerRows
        .flatMap((marker) => getTestsForDataPoint(marker.dataPoint.id))
        .filter((relatedTest) => relatedTest.id !== test.id)
        .map((relatedTest) => [relatedTest.id, relatedTest]),
    ).values(),
  );
  const testingOptions: PanelOption[] =
    testBody.category === "semen" ? semenPanelProducts : relatedTests;
  const openMarker = (marker: PanelMarker) => {
    if (!marker.markerTestId) {
      setActiveTab("values");
      return;
    }
    navigation.navigate(
      marker.markerTestIsSingleMarker ? "TestComparison" : "TestDetail",
      { testId: marker.markerTestId },
    );
  };

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 560,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={{
            ...GLASS_CARD_STYLE,
            paddingHorizontal: 24,
            paddingTop: 24,
            paddingBottom: 32,
          }}
        >
          <View style={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}>
            <IconButton
              onPress={handleBack}
              hitSlop={8}
              size={32}
              surface="soft"
              backgroundColor="rgba(0,0,0,0.05)"
              pressedBackgroundColor="rgba(0,0,0,0.05)"
              pressedOpacity={1}
              accessibilityLabel={t(
                "learn.article_actions.close_accessibility",
              )}
            >
              <X size={16} color="#9A8E85" />
            </IconButton>
          </View>

          <FactSheetHeader
            title={test.title}
            subtitle={test.subtitle}
            imageSource={getTestImage(
              test.id,
              primarySampleType,
              markerRows.map((marker) => marker.dataPoint),
              testBody.category,
            )}
            imageSize={86}
            badges={badges}
          />
          <FactSheetTabControl
            active={activeTab}
            onChange={setActiveTab}
            tabs={tabs}
          />

          {activeTab === "overview" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <View style={{ gap: 10 }}>
                <SectionTitleRow
                  title={t("access.testing.comparison.your_results")}
                  icon={History}
                  action={
                    isMultiMarkerPanel
                      ? hasPanelResult
                        ? {
                            label: t("access.testing.comparison.go_to_results"),
                            onPress: () => setActiveTab("values"),
                          }
                        : undefined
                      : {
                          label: t("all_lab_results.title_all"),
                          onPress: openAllResults,
                        }
                  }
                />
                {isMultiMarkerPanel ? (
                  <PanelOverviewSummaryRow
                    test={test}
                    markers={markerRows}
                    onResultPress={() => setActiveTab("values")}
                    onAddResult={openAllResults}
                    onGetTested={() => setActiveTab("testing")}
                    t={t}
                  />
                ) : (
                  <View style={{ gap: 8 }}>
                    {markerRows.map((marker) => (
                      <PanelOverviewResultRow
                        key={marker.dataPoint.id}
                        marker={marker}
                        onOpenDetails={(result, point) =>
                          setSelected({ result, point })
                        }
                        onAddResult={openAllResults}
                        t={t}
                      />
                    ))}
                  </View>
                )}
              </View>

              <InfoBlock
                icon={Heart}
                title={t("learn.test_detail.section_what_it_measures")}
              >
                {testBody.whatItMeasures}
              </InfoBlock>

              <View style={{ gap: 10 }}>
                <SectionTitle icon={Info}>
                  {t("access.testing.comparison.at_a_glance")}
                </SectionTitle>
                {testBody.quickFacts?.length ? (
                  <QuickFacts items={testBody.quickFacts} t={t} />
                ) : null}
              </View>

              <View style={{ gap: 10 }}>
                <SectionTitleRow
                  title={t("access.testing.comparison.markers_tracked_count", {
                    params: { count: String(markerRows.length) },
                  })}
                  icon={Beaker}
                  action={
                    markerRows.length > 6
                      ? {
                          label: showAllMarkers
                            ? t("test_result.show_less")
                            : t("care.view_all"),
                          onPress: () =>
                            setShowAllMarkers((current) => !current),
                        }
                      : undefined
                  }
                />
                <View
                  style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}
                >
                  {visibleMarkerRows.map((marker) => (
                    <PanelMarkerCard
                      key={marker.dataPoint.id}
                      marker={marker}
                      onPress={() => openMarker(marker)}
                      t={t}
                    />
                  ))}
                </View>
              </View>

              {testBody.howItWorks ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Wrench}>
                    {t("learn.test_detail.section_how_it_works")}
                  </SectionTitle>
                  <StepList items={howItWorksSteps} />
                </View>
              ) : null}

              {testBody.whyItMatters?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={Heart}>
                    {t("learn.test_detail.section_why_it_matters")}
                  </SectionTitle>
                  <BenefitList items={testBody.whyItMatters} />
                </View>
              ) : null}

              {test.sources?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={BookOpen}>
                    {t("learn.test_detail.section_evidence")}
                  </SectionTitle>
                  <EvidenceSourceRows
                    sources={test.sources}
                    onEvidenceLevelPress={setEvidenceLevel}
                  />
                </View>
              ) : null}

              {testBody.considerations?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={AlertCircle}>
                    {t("learn.test_detail.section_considerations")}
                  </SectionTitle>
                  {testBody.considerations.map((item) => (
                    <Text
                      key={item}
                      className="font-dm-sans"
                      style={{ fontSize: 13, lineHeight: 19, color: "#7D6F66" }}
                    >
                      • {item}
                    </Text>
                  ))}
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "values" ? (
            <View style={{ marginTop: 24, gap: 18 }}>
              <SectionTitleRow
                title={t("access.testing.comparison.your_results")}
                icon={History}
                action={{
                  label: t("all_lab_results.title_all"),
                  onPress: openAllResults,
                }}
              />
              {testResultsQuery.isLoading ? (
                <ActivityIndicator color="#6E625B" />
              ) : (
                <View style={{ gap: 22 }}>
                  <PanelAddResultRow onAddResult={openAllResults} t={t} />
                  {markerRows.map((marker) => (
                    <PanelMarkerValues
                      key={marker.dataPoint.id}
                      marker={marker}
                      onOpenDetails={(result, point) =>
                        setSelected({ result, point })
                      }
                      t={t}
                    />
                  ))}
                </View>
              )}
            </View>
          ) : null}

          {activeTab === "biology" ? (
            <View style={{ marginTop: 24, gap: 24 }}>
              <InfoBlock
                icon={Beaker}
                title={t("care.lab_result.biological_basis")}
              >
                {testBody.whatItMeasures}
              </InfoBlock>
              <InfoBlock
                icon={Wrench}
                title={t("care.lab_result.how_it_is_measured")}
              >
                {testBody.howItWorks}
              </InfoBlock>
              {testBody.cycleDay ? (
                <InfoBlock
                  icon={CalendarDays}
                  title={t("care.lab_result.when_it_is_measured")}
                >
                  {testBody.cycleDay}
                </InfoBlock>
              ) : null}
              {testBody.markerTiming?.length ? (
                <View style={{ gap: 10 }}>
                  <SectionTitle icon={CalendarDays}>
                    {t("learn.test_detail.section_marker_timing")}
                  </SectionTitle>
                  <MarkerTiming items={testBody.markerTiming} />
                </View>
              ) : null}
            </View>
          ) : null}

          {activeTab === "testing" ? (
            <View style={{ marginTop: 24, gap: 12 }}>
              <Text
                className="font-dm-sans"
                style={{ fontSize: 13, lineHeight: 19, color: "#7D6F66" }}
              >
                {testBody.category === "semen"
                  ? t("access.testing.comparison.semen_panel_options_body")
                  : t("access.testing.comparison.panel_related_tests_body")}
              </Text>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 12 }}>
                {testingOptions.map((option) => (
                  <TestOptionCard
                    key={option.id}
                    option={option}
                    t={t}
                    imageSource={getOptionImage(option)}
                    onPress={() =>
                      navigation.navigate("TestDetail", {
                        testId: String(option.id),
                      })
                    }
                  />
                ))}
              </View>
              {testingOptions.length === 0 ? (
                <Text
                  className="font-dm-sans"
                  style={{ fontSize: 13, color: "#9A8E85" }}
                >
                  {t("care.lab_result.testing_options_empty")}
                </Text>
              ) : null}
            </View>
          ) : null}

          <View
            style={{
              marginTop: 32,
              padding: 14,
              borderRadius: 12,
              backgroundColor: "rgba(0,0,0,0.03)",
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 11,
                color: "#9A8E85",
                lineHeight: 17,
                textAlign: "center",
              }}
            >
              {t("learn.disclaimer_medical_info")}
            </Text>
          </View>
        </View>
      </ScrollView>

      <TestResultDetailDialog
        result={selected?.result ?? null}
        point={selected?.point ?? null}
        onClose={() => setSelected(null)}
        onAttachSource={async (id, attachmentId) => {
          await updateTestResult.mutateAsync({
            testResultId: id,
            data: { attachmentId },
          });
        }}
        onEdit={handleEditResult}
        onDelete={handleDeleteResult}
      />
      <EvidenceLevelModal
        level={evidenceLevel}
        visible={evidenceLevel !== null}
        onClose={() => setEvidenceLevel(null)}
      />
    </GlassScreenLayout>
  );
}
